import { useState } from "react";
import { cdssApi, casesApi } from "@/lib/api";
import { triggerDownload, fileSafe } from "@/lib/download";
import { toast } from "sonner";

// Report / prescription / case-sheet downloads + case save, extracted from
// useWorkflow to keep the core state machine lean.
export function useCaseActions(ctx) {
  const { patient, findings, final, redFlag, differentials, investigations, reports, imaging } = ctx;
  const [downloading, setDownloading] = useState(false);
  const [downloadingRx, setDownloadingRx] = useState(false);
  const [downloadingSheet, setDownloadingSheet] = useState(false);
  const [savingCase, setSavingCase] = useState(false);
  const [savedId, setSavedId] = useState(null);

  const runDownload = (setFlag) => async (apiFn, filename, errMsg) => {
    setFlag(true);
    try {
      triggerDownload(await apiFn(), filename);
    } catch {
      toast.error(errMsg);
    } finally {
      setFlag(false);
    }
  };

  const name = fileSafe(patient.name);

  const downloadPdf = () => runDownload(setDownloading)(
    () => cdssApi.finalReportPdf({ patient, findings, final, red_flag: redFlag }),
    `CDSS_Report_${name}.pdf`, "Could not generate the PDF report.");

  const downloadRx = () => runDownload(setDownloadingRx)(
    () => cdssApi.prescriptionPdf({ patient, final, findings, red_flag: redFlag }),
    `Prescription_${name}.pdf`, "Could not generate the prescription.");

  const downloadCaseSheet = () => runDownload(setDownloadingSheet)(
    () => cdssApi.caseSheetPdf({ patient, findings, differentials, investigations, reports, imaging, final, red_flag: redFlag }),
    `CaseSheet_${name}.pdf`, "Could not generate the case sheet.");

  const saveCase = async () => {
    setSavingCase(true);
    try {
      const title = `${patient.name || "Case"} — ${final?.final_diagnosis?.condition || "Diagnosis"}`;
      const res = await casesApi.save({ title, patient, findings, differentials, investigations, reports, imaging, final, red_flag: redFlag });
      setSavedId(res.id);
      toast.success("Case saved to history");
    } catch {
      toast.error("Could not save the case.");
    } finally {
      setSavingCase(false);
    }
  };

  return { downloading, downloadingRx, downloadingSheet, savingCase, savedId, setSavedId, downloadPdf, downloadRx, downloadCaseSheet, saveCase };
}
